import { OnboardingPersonalization } from './OnboardingPersonalization.tsx';
import { parse } from 'url';

import { css } from '@compiled/react';
import React, {
	Fragment,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import type { ComponentProps, FC } from 'react';
import { useInView } from 'react-intersection-observer';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import UFOLabel from '@atlaskit/react-ufo/label';
import type { RendererAppearance } from '@atlaskit/renderer/types';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';
import { useReactProps_DO_NOT_USE } from '@atlassian/embedded-confluence/useReactProps';

import { isMobileApp_DO_NOT_USE } from '@confluence/mobile-app-detection-common';
import { resolveAgentTrustReceipt } from '@confluence/agent-trust-receipt/entry-points/resolveAgentTrustReceipt.ts';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';
import { AnnotationsProvider } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { DocumentUpdateStatusProvider } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';
import { ArchivedPageBanner } from '@confluence/archive-pages/entry-points/ArchivedPageBannerV2.ts';
import { getAMNReferralSourceFromQueryParam } from '@confluence/automated-meeting-notes/entry-points/getAMNReferralSourceFromQueryParam.ts';
import { useAutomatedMeetingNotesPageProperties } from '@confluence/automated-meeting-notes/entry-points/useAutomatedMeetingNotesPageProperties.ts';
import { ChoreographerAssetsContextProvider } from '@confluence/choreographer-services/entry-points/ChoreographerAssetsContext.ts';
import { ObjectCommentProvider } from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import { NewCommentsProvider } from '@confluence/comment-context/entry-points/NewCommentsContext.ts';
import { PageCommentsCountProvider } from '@confluence/comment-context/entry-points/PageCommentsCountContext.ts';
import { useInlineCommentsDispatchContext } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import { ReactionsProvider } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { CommentsDataBackgroundLoader } from '@confluence/comments-data-manager/entry-points/CommentsDataBackgroundLoader.tsx';
import { useProcessedCommentsDataActions } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { resetMediaViewerSidebar } from '@confluence/editor-features/entry-points/resetMediaViewerSidebar.ts';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { PageCommentsPubSubHandler } from '@confluence/comments-pubsub/entry-points/pageComments.ts';
import { ViewCommentShortcutListener } from '@confluence/comments-shortcuts/entry-points/ViewCommentShortcutListener.ts';
import { ContentViewedEvent } from '@confluence/content-body/entry-points/ContentViewedEvent.ts';
import { ContentRefetchProvider } from '@confluence/content-body/entry-points/ContentRefetchContext.ts';
import { ContentOwnershipContextProvider } from '@confluence/content-ownership/entry-points/ContentOwnershipContext.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { ReadingAidsAcronymsHighlighter } from '@confluence/contextual-reading-aids/entry-points/readingAidsAcronymsHighlighter.ts';
import { OmniBarWrapper } from '@confluence/experiment-omnibar/entry-points/OmniBarWrapper.ts';
import {
	CUSTOM_HEADER_FOOTER_TYPES,
	CustomHeaderFooterLoader,
} from '@confluence/custom-header-footer';
import { AutoConversionByLineContextProvider } from '@confluence/editor-conversion/entry-points/AutoConversionByLineContext.ts';
import { getAutoConvertionInfo } from '@confluence/editor-conversion/entry-points/getAutoConvertionInfo.ts';
import { canAddCommentsFromOperations } from '@confluence/entity-operations/entry-points/canAddCommentsFromOperations.ts';
import {
	LIVE_PAGE_PUBLISH_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
	LIVE_PAGE_SESSION_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { getPreviousRouteExperienceData } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import {
	expVal,
	expValEquals,
	UNSAFE_expValEqualsNoExposure,
	UNSAFE_isExperimentEnabledNoExposure,
} from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { OmnibarEligibilityProvider } from '@confluence/experiment-omnibar/entry-points/OmnibarEligibilityContext.ts';
import { useOmnibarEligibility } from '@confluence/experiment-omnibar/entry-points/useOmnibarEligibility.ts';
import { useIsReactionsEnabled } from '@confluence/comments-hooks/entry-points/useIsReactionsEnabled.ts';
import {
	ImportFeedbackBanner,
	getImportDate,
	getImportSource,
	isImportBannerEnabled,
} from '@confluence/import-feedback';
import { InlineCommentsHighlighterLoader } from '@confluence/inline-comments/entry-points/InlineCommentsHighlighterLoader.ts';
import { useSetContentLastModifiedTimestamp } from '@confluence/issue-create-side-panel/entry-points/BulkCreateContextProvider.ts';
import { ViewPageLabelsLoader } from '@confluence/labels/entry-points/ViewPageLabelsLoader.ts';
import { LivePageComponentLoader as LivePageComponentClient } from '@confluence/live-pages/entry-points/LivePageComponent.ts';
import { LivePageComponentServer } from '@confluence/live-pages/entry-points/LivePageComponentServer.ts';
import { ConvertToLivePageFlag } from '@confluence/live-pages-features/entry-points/ConvertToLivePageFlag.tsx';
import { MovePageSuccessFlag } from '@confluence/page-tree/entry-points/MovePageSuccessFlag.tsx';
import { LIVE_PAGE_SUBTYPE } from '@confluence/live-pages-utils';
import type { CachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useLivePageStoreActions } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { LoadingPriority } from '@confluence/loadable';
import { useMentionsNotificationsActions } from '@confluence/mentions-notifications/entry-points/useMentionsNotifications.ts';
import { LocalActivityViewPageTracker } from '@confluence/local-activity/entry-points/LocalActivityViewPageTracker.ts';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { NativeCollabPresenceStateContainer } from '@confluence/native-collab/entry-points/useNativeCollabPresenceState.ts';
import { StickyDraftCommentContainer } from '@confluence/object-comments/entry-points/StickyDraftCommentContainer.ts';
import { ObjectCommentWrapper } from '@confluence/object-comments/entry-points/ObjectCommentWrapper.ts';
import { ObjectSidebarControl } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarControl.tsx';
import { useHasAudioButton } from '@confluence/object-sidebar-components/entry-points/useHasAudioButton.ts';
import { RendererPageCoverPicture } from '@confluence/page-cover-picture/entry-points/RendererPageCoverPicture.ts';
import { getRendererCoverPictureInfo } from '@confluence/page-cover-picture/entry-points/coverPictureHelperFunctions.ts';
import { EndOfPageRecommendationFinalLoader } from '@confluence/page-recommendations/entry-points/EndOfPageRecommendationLoader.ts';
import { QuickReload } from '@confluence/quick-reload/entry-points/QuickReload.ts';
import { PageMode } from '@confluence/quick-reload/entry-points/pubSubConstants.ts';
import { RendererActionsProvider } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';
import { ScrollToPageStart } from '@confluence/scroll/entry-points/ScrollToPageStart.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { setIsLivePage } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';
import {
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { useConnectAppsData } from '@confluence/web-panel-location/entry-points/useConnectAppsData.ts';
import { useAmplitudeSessionReplay } from '@confluence/amplitude-session-replay/entry-points/AmplitudeSessionReplay.ts';
import { TeammateContentRecommendationBanner } from '@confluence/experiment-teammate-content-screen-page/entry-points/TeammateContentRecommendationBanner.ts';
import { CwrWelcomeBanner } from '@confluence/experiment-cwr-nth-user-onboarding/entry-points/CwrWelcomeBanner.ts';

import { ViewPageContributors } from './ViewPageContributors.tsx';
import type { BaseViewPageProps } from './BaseViewPageProps.ts';
import { ContentTitleWithByLine } from './ContentTitleWithByLine.tsx';
import {
	CurrentContentBodyDynamicTracker,
	type OnCurrentContentBodyDynamicChange,
} from './CurrentContentBodyDynamicTracker.tsx';
import { CurrentContentBodyDynamicUnavailableTracker } from './CurrentContentBodyDynamicUnavailableTracker.tsx';
import { SatisfactionSurveysLoader } from './SatisfactionSurveysLoader.ts';
import { ViewPageContainer } from './ViewPageContainer.tsx';
import { ViewPageCommentsPanelPermission } from './ViewPageCommentsPanelPermission.tsx';
import type { ViewPageContentLoader } from './ViewPageContentLoader.tsx';
import { ViewPageInlineComments } from './ViewPageInlineComments.tsx';
import { useRenderLivePageEarly } from './useRenderLivePageEarly.tsx';
import { ViewPageContentWithStatus } from './ViewPageContent.tsx';
import { ViewPageMainContentContainer } from './ViewPageMainContentContainer.tsx';
import { ViewPageSplitTinyUFO } from './ViewPageSplitTinyUFO.tsx';
import { LivePageStateSync } from './LivePageStateSync.tsx';

// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
const LivePageComponent = process.env.REACT_SSR ? LivePageComponentServer : LivePageComponentClient;

// Rough estimate to determine if the page is small enough that the footer sections (comments, end-of-page recs) are likely visible in the initial viewport. Used to prevent the SSR'd omnibar from flashing on small pages where useInView hooks haven't settled yet during hydration.
const SMALL_PAGE_ADF_LENGTH_THRESHOLD = 2000;
export const setLoadingPriority = (
	hasAllowedFeature: boolean,
): LoadingPriority.PAINT | LoadingPriority.LAZY | null => {
	if (isEmbeddedConfluence_DO_NOT_USE() || isMobileApp_DO_NOT_USE()) {
		return hasAllowedFeature ? LoadingPriority.PAINT : LoadingPriority.LAZY;
	}
	return null;
};

type ContentTitleAndByLineOptions = Pick<
	ComponentProps<typeof ContentTitleWithByLine>,
	'hasByLineContributors' | 'hasByLineExtensions' | 'headingContainer'
>;

type Props = BaseViewPageProps & {
	contentHeader?: ComponentProps<typeof ViewPageContainer>['stickyHeader'];
	hasDocumentContractContentHeader?: boolean;
	hasByLine?: boolean;
	hasPageComments?: boolean;
	hasSatisfactionSurveys?: boolean;
	hasInlineComments?: boolean;
	hasProgressiveRendering?: boolean;
	hasLabels?: boolean;
	hasReactions?: boolean;
	hasEOPRecs?: boolean;
	hasQuickReload?: boolean;
	appearance?: string;
	hasInlineActions?: boolean;
	contentScreenStyles?: ComponentProps<typeof ViewPageContainer>['contentScreenStyles'];
	eventHandlers?: React.ComponentProps<typeof ViewPageContentLoader>['eventHandlerOverrides'];
	titleAndByLineOptions?: ContentTitleAndByLineOptions;
	children?: React.ReactNode;
	setHasCoverPicture?: (hasCoverPicture: boolean) => void;
	isEmbeddedPage?: boolean;
	ClassicEditorContextProviderLoader?: React.ComponentType<any>;
	renderLivePageAsViewPage?: boolean;
	cachedPageData?: CachedPageData;
	disableTableOverflowShadow?: boolean;
	// Reports body.dynamic for the currently rendered content without duplicating ViewPageContainer's
	// ContentUnifiedQuery path; fast live-page rendering reports the current contentId with no body.
	// Consumed by EmbeddedViewPage to expose the current page ADF to the native embed read-content
	// command (see embedded-confluence). Keep while that consumer exists.
	onCurrentContentBodyDynamicChange?: OnCurrentContentBodyDynamicChange;
};

const hideDuringPdfPrint = css({
	'@media print': {
		display: 'none',
	},
});

const FooterLoader: FC = () => {
	return <CustomHeaderFooterLoader type={CUSTOM_HEADER_FOOTER_TYPES.FOOTER} />;
};

type CommentsPanelPermissionProps = Pick<
	ComponentProps<typeof ViewPageCommentsPanelPermission>,
	'contentFragmentRef'
>;

const ViewPageObjectSidebarControl: FC<
	ComponentProps<typeof ObjectSidebarControl> &
		CommentsPanelPermissionProps & { isEmbeddedPage?: boolean; isLivePage?: boolean }
> = ({ contentFragmentRef, contributorsFragmentRef, isEmbeddedPage, isLivePage, ...props }) => (
	<ViewPageContributors
		contentId={props.contentId}
		contentIdFromQuery={props.contentIdFromQuery}
		contributorsFragmentRef={contributorsFragmentRef}
		isEmbeddedPage={isEmbeddedPage}
		isLivePage={isLivePage}
	>
		{(eligibleContributorsRef) => (
			<ViewPageCommentsPanelPermission contentFragmentRef={contentFragmentRef}>
				{(preloadedCanAddComments) => (
					// @ts-ignore -- Downstream packages are checked against the last generated object-sidebar-components declaration.
					<ObjectSidebarControl
						{...props}
						contributorsFragmentRef={eligibleContributorsRef}
						preloadedCanAddComments={preloadedCanAddComments}
					/>
				)}
			</ViewPageCommentsPanelPermission>
		)}
	</ViewPageContributors>
);

const ViewPageQuickReload: FC<
	Omit<ComponentProps<typeof QuickReload>, 'showCommentsPanel'> & CommentsPanelPermissionProps
> = ({ contentFragmentRef, ...props }) => (
	<ViewPageCommentsPanelPermission contentFragmentRef={contentFragmentRef}>
		{(preloadedCanAddComments) => (
			<ViewPageQuickReloadWithPermission
				{...props}
				preloadedCanAddComments={preloadedCanAddComments}
			/>
		)}
	</ViewPageCommentsPanelPermission>
);

const ViewPageQuickReloadWithPermission: FC<
	Omit<ComponentProps<typeof QuickReload>, 'showCommentsPanel'> & {
		preloadedCanAddComments?: boolean;
	}
> = ({ preloadedCanAddComments, ...props }) => {
	// @ts-ignore -- Downstream packages are checked against the last generated comments-panel declaration.
	const { showCommentsPanel } = useShowCommentsPanel(preloadedCanAddComments);

	return <QuickReload {...props} showCommentsPanel={showCommentsPanel} />;
};

// NOTE: `contentId` represents the user's intended page but not always the displayed one. During transition to a new page, the component still renders the previous page until ViewPageContainer updates the contentQueryData.
//       While fetching new contentQueryData, the prior contentQueryData is displayed, causing a brief mismatch between contentId and the contentQueryData's contentId.
const ViewPageCommonInner: FC<Props> = ({
	spaceKey,
	contentId,
	contentHeader,
	hasDocumentContractContentHeader = false,
	contentScreenStyles,
	eventHandlers,
	titleAndByLineOptions,
	hasByLine = true,
	hasPageComments = true,
	hasSatisfactionSurveys = true,
	hasInlineComments = true,
	hasInlineActions = true,
	hasLabels = true,
	hasReactions = true,
	hasEOPRecs = true,
	hasQuickReload = true,
	hasProgressiveRendering,
	appearance,
	children,
	setHasCoverPicture,
	isEmbeddedPage,
	ClassicEditorContextProviderLoader,
	renderLivePageAsViewPage = false,
	cachedPageData = {},
	disableTableOverflowShadow,
	onCurrentContentBodyDynamicChange,
}) => {
	const { locale } = useSessionData();
	const isRenderServerPlaceholder = useIsSSROrHydrating();
	const setContentLastModifiedTimestamp = useSetContentLastModifiedTimestamp();
	const { isLivePage: isLivePageOnSSR } = getSSRRenderInputs();
	const { resetInitialDataLoadedMap } = useCommentsPanelActions();
	const { clearCommentsData } = useCommentsDataActions();
	const { clearProcessedData } = useProcessedCommentsDataActions();
	const { clearMentionsFlags } = useMentionsNotificationsActions();
	const experienceTracker = useContext(ExperienceTrackerContext) as ExperienceTrackerAPI;
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId,
	});
	let { embeddedConfluenceSource } = useReactProps_DO_NOT_USE() || {};
	if (
		!embeddedConfluenceSource &&
		isEmbeddedPage &&
		!isRenderServerPlaceholder &&
		typeof location !== 'undefined'
	) {
		embeddedConfluenceSource = parse(
			location.search,
			true,
		).query.embeddedConfluenceSource?.toString();
	}

	const isPdfExport = PDF_EXPORT_PAGE.match(window.location.pathname);
	const [quickReloadReady, setQuickReloadReady] = useState(false);
	const quickReloadLastPollTimeRef = useRef<number | null>(null);

	useEffect(() => {
		if (!hasQuickReload) {
			return;
		}

		if (quickReloadLastPollTimeRef.current === null) {
			quickReloadLastPollTimeRef.current = Date.now();
		}

		setQuickReloadReady(true);
	}, [hasQuickReload]);

	const isReadingAidsAutoHighlightEnabled =
		(isAIEnabledForContent || isRovoEnabledForContent) && locale.startsWith('en');

	// Setup for A/A experiment for testing stratified sampling with Statsig
	expVal('cc_stratified_sampling_exp_aa', 'cohort', 'not-controlled');

	let stickyHeader = contentHeader;

	const isLivePage = useIsCurrentPageLive();
	const { setIsLive } = useLivePageStoreActions();
	const omnibarEligibility = useOmnibarEligibility(isLivePage || !!isPdfExport || !!isEmbeddedPage);
	const isReactionsEnabled = useIsReactionsEnabled(contentId);
	const [pageCommentsSectionRef, pageCommentsSectionInView] = useInView();
	const [endOfPageRecRef, endOfPageRecInView] = useInView();
	const [hasInViewSettled, setHasInViewSettled] = useState(false);
	useEffect(() => {
		const frame = requestAnimationFrame(() => {
			setHasInViewSettled(true);
		});
		return () => cancelAnimationFrame(frame);
	}, []);

	// Refs for omnibar small-page heuristic instrumentation (false positive detection).
	const suppressedDueToSmallPageHeuristicRef = useRef(false);
	const lastSuppressedAdfLengthRef = useRef<number>(0);
	const hasFiredOmnibarHeuristicEventRef = useRef(false);

	const { setActiveHighlight } = useInlineCommentsDispatchContext();

	const shouldShowBylineReactions = hasReactions && !isLivePage;

	const setLivePageGlobalState = useCallback(
		(isContentLive: boolean) => {
			void setIsLive(isContentLive);
			void setIsLivePage(isContentLive); // page load state used for analytics context (e.g. for experience tracking)
		},
		[setIsLive],
	);

	useAmplitudeSessionReplay({
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		skip: !fg('cc_engagement_amplitude_session_replay_gate'),
	});

	useEffect(() => {
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		if (fg('confluence_live_doc_conversion_stability')) return;
		if (!isLivePage) return;

		return () => {
			setLivePageGlobalState(false);
		};
	}, [isLivePage, setLivePageGlobalState]);

	useEffect(() => {
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		if (fg('confluence_live_doc_conversion_stability')) {
			// Only reset committed live state when leaving ViewPageCommon.
			return () => setLivePageGlobalState(false);
		}
	}, [setLivePageGlobalState]);

	// Update view-page experience with `isLivePage` attribute to ensure all future experience fails denote whether it was on a live page
	useEffect(() => {
		const experienceState = experienceTracker.getExperienceState(VIEW_PAGE_EXPERIENCE);

		if (
			experienceState?.attributes?.contentId === contentId &&
			experienceState?.attributes?.isLivePage !== isLivePage
		) {
			experienceTracker.updateAttributes({
				name: VIEW_PAGE_EXPERIENCE,
				attributes: { isLivePage },
			});
		}
	}, [contentId, isLivePage, experienceTracker]);

	// stickyHeader needs to be removed based on live page state since it's used in ViewPageContainer props
	if (isLivePage) {
		stickyHeader = undefined;
	}
	const {
		renderLivePageEarly,
		isLivePageCacheOutdated,
		isFastLoadedLivePageArchived,
		isLivePageFastLoadedFromSSR,
	} = useRenderLivePageEarly({
		contentId,
		cachedSubType: cachedPageData.subType,
		cachedIsArchived: cachedPageData.isArchived,
		renderLivePageAsViewPage,
	});

	useEffect(() => {
		if (renderLivePageEarly) {
			setLivePageGlobalState(true);
		}
	}, [renderLivePageEarly, setLivePageGlobalState]);

	useEffect(() => {
		return () => {
			resetInitialDataLoadedMap();
			clearCommentsData();
			clearProcessedData();
			clearMentionsFlags();
			setActiveHighlight(null);
			if (UNSAFE_expValEqualsNoExposure('cc_comments_media_viewer_sidebar', 'isEnabled', true)) {
				resetMediaViewerSidebar();
			}
			hasFiredOmnibarHeuristicEventRef.current = false;
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId]);

	let livePageFastLoadedSource = '';
	if (renderLivePageEarly) {
		if (isLivePageFastLoadedFromSSR) {
			livePageFastLoadedSource = 'SSR';
		} else if (cachedPageData?.gqlType) {
			livePageFastLoadedSource = `GQL:${cachedPageData.gqlType}`;
		} else {
			livePageFastLoadedSource = 'Unknown';
		}
	}

	const { successfulExperiences: previousRouteSuccessfulExperiences } =
		getPreviousRouteExperienceData();
	const livePagePerfData = useMemo(
		() => ({
			renderLivePageEarly,
			isLivePageCacheOutdated,
			isFastLoadedLivePageArchived,
			isLivePageFastLoadedFromSSR,
			livePageFastLoadedSource,
			contentExtensionMetrics: {},
			previousRouteSuccessfulExperiences: Array.from(previousRouteSuccessfulExperiences),
		}),
		[
			renderLivePageEarly,
			isLivePageCacheOutdated,
			isFastLoadedLivePageArchived,
			isLivePageFastLoadedFromSSR,
			livePageFastLoadedSource,
			previousRouteSuccessfulExperiences,
		],
	);

	const panelsModuleKeysAtlGeneral = useConnectAppsData({
		contentId,
		location: ATL_GENERAL,
	});
	const panelsModuleKeysAtlFooter = useConnectAppsData({
		contentId,
		location: ATL_FOOTER,
	});

	const customPerfData = {
		...livePagePerfData,
		webPanelsAtlGeneral: panelsModuleKeysAtlGeneral,
		webPanelsAtlFooter: panelsModuleKeysAtlFooter,
		...(embeddedConfluenceSource ? { embeddedConfluenceSource } : {}),
	};

	const {
		isAMN,
		isLoading: isAMNPagePropertiesLoading,
		isRecurringParent,
		isLiveAMNPage,
	} = useAutomatedMeetingNotesPageProperties(contentId);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasAudioButton = useHasAudioButton(isAIEnabledForContent);

	useEffect(() => {
		// maintain analytics for AMN page views without triggering spotlight
		if (!isAMNPagePropertiesLoading && isAMN) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'viewAMNPage',
					attributes: {
						isLivePage: isLiveAMNPage,
						isRecurringParent,
						referralSource: getAMNReferralSourceFromQueryParam(),
					},
				},
			}).fire();
		}
	}, [
		contentId,
		isAMN,
		isRecurringParent,
		isLiveAMNPage,
		isAMNPagePropertiesLoading,
		createAnalyticsEvent,
	]);

	// Fire track event when omnibar small-page heuristic settles to detect false positives.
	useEffect(() => {
		if (!hasInViewSettled || !suppressedDueToSmallPageHeuristicRef.current) return;

		const wasFalsePositive = !pageCommentsSectionInView && !endOfPageRecInView;
		const adfLength = lastSuppressedAdfLengthRef.current;

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'viewPageCommon',
				action: wasFalsePositive ? 'falsePositive' : 'truePositive',
				actionSubject: 'omniBar',
				actionSubjectId: 'omniBarSmallPageHeuristic',
				attributes: {
					contentId,
					adfLength,
					commentsSectionInView: pageCommentsSectionInView,
					endOfPageRecInView,
				},
			},
		}).fire();

		suppressedDueToSmallPageHeuristicRef.current = false;
		hasFiredOmnibarHeuristicEventRef.current = true;
	}, [
		hasInViewSettled,
		pageCommentsSectionInView,
		endOfPageRecInView,
		contentId,
		createAnalyticsEvent,
	]);

	return (
		<Fragment>
			<ContentOwnershipContextProvider>
				<ObjectCommentProvider>
					<NewCommentsProvider>
						<PageCommentsCountProvider contentId={contentId}>
							<DocumentUpdateStatusProvider>
								<ContentRefetchProvider>
									<RendererActionsProvider>
										<AnnotationsProvider>
											<ChoreographerAssetsContextProvider>
												{renderLivePageEarly ? (
													<Fragment>
														<CurrentContentBodyDynamicUnavailableTracker
															contentId={contentId}
															onCurrentContentBodyDynamicChange={onCurrentContentBodyDynamicChange}
														/>
														<NativeCollabPresenceStateContainer scope={contentId}>
															{/* oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates */}
															{fg('cc-engagement-nth-user-teammate-rollout-gate') && (
																<>
																	<TeammateContentRecommendationBanner
																		contentId={contentId}
																		isLivePage
																	/>
																	<CwrWelcomeBanner contentId={contentId} isLivePage />
																</>
															)}
															<LivePageComponent
																contentId={contentId}
																spaceKey={spaceKey}
																ClassicEditorContextProviderLoader={
																	ClassicEditorContextProviderLoader
																}
																customPerfData={customPerfData}
																loadingPriority={setLoadingPriority(true)}
																isFastLoaded
																hasByLineContributors={
																	titleAndByLineOptions?.hasByLineContributors ?? true
																}
																hasByLineExtensions={
																	titleAndByLineOptions?.hasByLineExtensions ?? true
																}
																hasReactions={hasReactions}
																hasPageComments={hasPageComments}
																hasInlineComments={hasInlineComments}
															/>
														</NativeCollabPresenceStateContainer>
													</Fragment>
												) : (
													<Fragment>
														<ViewPageContainer
															spaceKey={spaceKey}
															contentId={contentId}
															stickyHeader={stickyHeader}
															contentScreenStyles={contentScreenStyles}
															customPerfData={customPerfData}
															isEmbeddedPage={isEmbeddedPage}
															hasDocumentContractAnalyticsByline
															hasDocumentContractContentHeader={hasDocumentContractContentHeader}
														>
															{({
																isThemed,
																contentQueryData,
																contentQueryError,
																contentQueryRefetch,
															}) => {
																const resolvedContentHeader =
																	typeof contentHeader === 'function'
																		? contentHeader(contentQueryData)
																		: contentHeader;
																const contentStatus = contentQueryData?.content?.nodes?.[0]?.status;
																const contentType = contentQueryData?.content?.nodes?.[0]?.type;
																const contentSubType =
																	contentQueryData?.content?.nodes?.[0]?.subType;
																const contentTitle = contentQueryData?.content?.nodes?.[0]?.title;
																const spaceId = contentQueryData?.content?.nodes?.[0]?.space?.id;
																const contentIdFromQuery =
																	contentQueryData?.content?.nodes?.[0]?.id;
																const preloadedCanAddComments =
																	contentIdFromQuery === contentId &&
																	expVal(
																		'confluence_inline_comment_relay_content_ops',
																		'isEnabled',
																		false,
																	)
																		? canAddCommentsFromOperations(
																				contentQueryData?.content?.nodes?.[0]?.operations,
																			)
																		: undefined;
																const contentBodyDynamic =
																	contentQueryData?.content?.nodes?.[0]?.body?.dynamic;
																const lastModifiedDate =
																	contentQueryData?.content?.nodes?.[0]?.metadata?.lastModifiedDate;
																void setContentLastModifiedTimestamp(lastModifiedDate);
																const pageWidthType =
																	contentQueryData?.content?.nodes?.[0]?.appearancePublished
																		?.nodes?.[0]?.value || `"default"`;
																const isFabricPage = Boolean(
																	contentQueryData?.content?.nodes?.[0]?.body?.dynamic
																		?.representation === 'atlas_doc_format',
																);
																const showAIButton =
																	(contentType === 'page' || contentType === 'blogpost') &&
																	!isEmbeddedPage;
																const shouldShowEOPRecommendations =
																	hasEOPRecs &&
																	(contentType === 'page' || contentType === 'blogpost') &&
																	!(
																		isEmbeddedPage &&
																		UNSAFE_isExperimentEnabledNoExposure('cc-maui-experiment')
																	);

																const adfValue =
																	contentQueryData?.content?.nodes?.[0]?.body?.dynamic?.value;
																const isLikelySmallPage =
																	typeof adfValue === 'string' &&
																	adfValue.length < SMALL_PAGE_ADF_LENGTH_THRESHOLD;
																const shouldSuppressOmnibarForSmallPage =
																	!hasInViewSettled &&
																	isLikelySmallPage &&
																	(hasPageComments || shouldShowEOPRecommendations);
																// Spark-only Agent Trust Receipt preview wiring. Real page data hydrates the
																// CREATED_BY_AI receipt; all fields are already loaded by ContentUnifiedQuery.
																const contentNode = contentQueryData?.content?.nodes?.[0];
																// Downstream packages are checked against the last generated content-unified-query declaration.
																const contentQueryDataWithAnalyticsByline = contentQueryData as
																	| (typeof contentQueryData & {
																			readonly analyticsBylineFragment?: unknown;
																	  })
																	| undefined;
																// Downstream packages are checked against the last generated content-unified-query declaration.
																const contentNodeWithBylineClassification =
																	contentNode as typeof contentNode & {
																		readonly bylineClassificationFragment?: ComponentProps<
																			typeof ContentTitleWithByLine
																		>['bylineClassificationFragmentRef'];
																	};
																const commentsPanelPermissionFragmentRef =
																	contentIdFromQuery === contentId
																		? // @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
																			contentNode?.commentsPanelPermission
																		: undefined;
																const agentTrustReceipt =
																	isFabricPage &&
																	contentIdFromQuery &&
																	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
																	fg('cc_sparks_agent_trust_receipt')
																		? resolveAgentTrustReceipt(contentIdFromQuery, {
																				generatedBy:
																					contentNode?.contentProperties?.latest?.generatedBy,
																				creatorName: contentNode?.history?.createdBy?.displayName,
																				createdAt: contentNode?.history?.createdDate,
																				lastUpdatedAt: contentNode?.version?.when,
																				versionNumber: contentNode?.version?.number,
																			})
																		: null;

																if (shouldSuppressOmnibarForSmallPage) {
																	suppressedDueToSmallPageHeuristicRef.current = true;
																	lastSuppressedAdfLengthRef.current =
																		typeof adfValue === 'string' ? adfValue.length : 0;
																}

																if (
																	isLikelySmallPage &&
																	(hasPageComments || shouldShowEOPRecommendations) &&
																	!suppressedDueToSmallPageHeuristicRef.current &&
																	!hasFiredOmnibarHeuristicEventRef.current
																) {
																	suppressedDueToSmallPageHeuristicRef.current = true;
																	lastSuppressedAdfLengthRef.current =
																		typeof adfValue === 'string' ? adfValue.length : 0;
																}

																// getting info cover picture needs
																const {
																	coverPicture,
																	contentAppearance,
																	hasCoverPicture,
																	coverPictureWidth,
																} = getRendererCoverPictureInfo(contentQueryData, false);
																// getting info for auto conversion
																const {
																	queryDataId,
																	editorProperty,
																	isAutoConverted,
																	fabricEditorSupported,
																} = getAutoConvertionInfo(contentQueryData?.content?.nodes?.[0]);

																// If live page is archived, we don't show LivePageComponent. Instead, archived live pages will render using the existing ViewPageContentRenderer.
																const isContentLive =
																	(isLivePageOnSSR &&
																		(isRenderServerPlaceholder ||
																			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
																			!fg('confluence_live_doc_conversion_stability'))) ||
																	(contentQueryData?.content?.nodes?.[0]?.subType ===
																		LIVE_PAGE_SUBTYPE &&
																		contentStatus !== 'archived');

																// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
																if (!fg('confluence_live_doc_conversion_stability')) {
																	setLivePageGlobalState(isContentLive);
																}

																// Content property values are JSON-stringified by the API;
																// contentQueryData may be page or blog union; blog nodes lack
																// agentSessionsMarker, so narrow with 'in' before accessing.
																const cuqNode = contentQueryData?.content?.nodes?.[0];
																const agentSessionsMarkerValue =
																	cuqNode != null && 'agentSessionsMarker' in cuqNode
																		? cuqNode.agentSessionsMarker?.nodes?.[0]?.value
																		: undefined;
																// Gate on the experiment as well as the marker so this acts as a
																// kill-switch: turning cc_ai_agent_mention_from_comment off makes
																// hasCommentSessions false everywhere without reverting code.
																const hasCommentSessions =
																	agentSessionsMarkerValue === 'true' &&
																	expValEquals(
																		'cc_ai_agent_mention_from_comment',
																		'isEnabled',
																		true,
																	);

																if (
																	isContentLive &&
																	!contentQueryError &&
																	!!contentIdFromQuery &&
																	!renderLivePageAsViewPage
																) {
																	return (
																		<Fragment>
																			{/* oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates */}
																			{fg('confluence_live_doc_conversion_stability') && (
																				<LivePageStateSync
																					contentId={contentId}
																					isLive={isContentLive}
																					onChange={setLivePageGlobalState}
																				/>
																			)}
																			<CurrentContentBodyDynamicTracker
																				contentId={contentId}
																				contentIdFromQuery={contentIdFromQuery}
																				contentBodyDynamic={contentBodyDynamic}
																				onCurrentContentBodyDynamicChange={
																					onCurrentContentBodyDynamicChange
																				}
																			/>
																			<NativeCollabPresenceStateContainer scope={contentId}>
																				{/* oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates */}
																				{fg('cc-engagement-nth-user-teammate-rollout-gate') && (
																					<>
																						<TeammateContentRecommendationBanner
																							contentId={contentIdFromQuery}
																							isLivePage
																						/>
																						<CwrWelcomeBanner
																							contentId={contentIdFromQuery}
																							isLivePage
																						/>
																					</>
																				)}
																				<LivePageComponent
																					contentId={contentIdFromQuery}
																					spaceKey={spaceKey}
																					ClassicEditorContextProviderLoader={
																						ClassicEditorContextProviderLoader
																					}
																					customPerfData={customPerfData}
																					isFastLoaded={false}
																					hasByLineContributors={
																						titleAndByLineOptions?.hasByLineContributors ?? true
																					}
																					hasByLineExtensions={
																						titleAndByLineOptions?.hasByLineExtensions ?? true
																					}
																					hasReactions={hasReactions}
																					hasPageComments={hasPageComments}
																					hasInlineComments={hasInlineComments}
																					hasCommentSessions={hasCommentSessions}
																				/>
																			</NativeCollabPresenceStateContainer>
																		</Fragment>
																	);
																}

																if (!!setHasCoverPicture) {
																	setHasCoverPicture(hasCoverPicture);
																}

																const livePagePublishExperienceState =
																	experienceTracker.getExperienceState(
																		LIVE_PAGE_PUBLISH_EXPERIENCE,
																	);
																if (
																	livePagePublishExperienceState &&
																	!livePagePublishExperienceState.hasStopped &&
																	livePagePublishExperienceState.attributes?.contentId === contentId
																) {
																	// Succeed the live-page/publish and live-page-session experiences on render of view page content after conversion to page
																	experienceTracker.succeed({
																		name: LIVE_PAGE_PUBLISH_EXPERIENCE,
																	});

																	experienceTracker.succeed({
																		name: LIVE_PAGE_SESSION_EXPERIENCE,
																		attributes: {
																			convertedToPage: true,
																		},
																	});
																}

																// Relay results are readonly; import helpers use a mutable ContentQueryData shape.
																// Cast avoids generic T inference fighting over page vs blog query unions.
																const importedContentSource = getImportSource(
																	contentQueryData as Parameters<typeof getImportSource>[0],
																);
																const importedContentDate = getImportDate(
																	contentQueryData as Parameters<typeof getImportDate>[0],
																);
																const isImportBannerShown =
																	isImportBannerEnabled(
																		contentQueryData as Parameters<typeof isImportBannerEnabled>[0],
																	) &&
																	importedContentSource &&
																	importedContentDate;

																const maybeScrollToPageStart =
																	Boolean(window.__SSR_RENDERED__) &&
																	expVal(
																		'confluence_p2m_style_recalc_and_expand_joint_exp',
																		'isEnabled',
																		false,
																	) ? null : (
																		<ScrollToPageStart
																			key={`ScrollToPageStart-${contentId}`}
																			isSmooth={false}
																		/>
																	);

																return (
																	<Fragment>
																		{/* oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates */}
																		{fg('confluence_live_doc_conversion_stability') && (
																			<LivePageStateSync
																				contentId={contentId}
																				isLive={isContentLive}
																				onChange={setLivePageGlobalState}
																			/>
																		)}
																		{maybeScrollToPageStart}
																		{/* oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates */}
																		{fg('cc-engagement-nth-user-teammate-rollout-gate') && (
																			<>
																				<TeammateContentRecommendationBanner
																					contentId={contentId}
																					isLivePage={false}
																				/>
																				<CwrWelcomeBanner
																					contentId={contentId}
																					isLivePage={false}
																				/>
																			</>
																		)}
																		<ViewPageMainContentContainer
																			isFabricPage={isFabricPage}
																			isEmbeddedPage={isEmbeddedPage}
																		>
																			{/* ViewPageMainContentContainer should contain all page content above footer comments */}
																			{isThemed && resolvedContentHeader}
																			<ViewPageSplitTinyUFO isFabricPage={isFabricPage} />
																			<CustomHeaderFooterLoader
																				type={CUSTOM_HEADER_FOOTER_TYPES.HEADER}
																			/>
																			{isImportBannerShown && (
																				<ImportFeedbackBanner
																					importSource={importedContentSource}
																					importDate={importedContentDate}
																					contentId={contentId}
																				/>
																			)}
																			{hasCoverPicture && (
																				<RendererPageCoverPicture
																					contentId={contentId}
																					isPagePreview={false}
																					coverPicture={coverPicture}
																					contentAppearance={contentAppearance}
																					isSpaceOverview={false}
																					coverPictureWidthOverride={coverPictureWidth}
																					titleContentFragmentRef={
																						contentQueryData?.content?.nodes?.[0]
																							?.rendererTitleContentProperties
																					}
																					coverPictureFragmentRef={
																						contentQueryData?.content?.nodes?.[0]
																							?.rendererCoverPicture
																					}
																				/>
																			)}
																			<AutoConversionByLineContextProvider
																				editorProperty={editorProperty}
																				fabricEditorSupported={fabricEditorSupported}
																				isAutoConverted={!!isAutoConverted}
																				queryDataId={queryDataId}
																			>
																				<ReactionsProvider contentId={contentId}>
																					<ContentTitleWithByLine
																						banner={
																							<ArchivedPageBanner
																								contentId={contentId}
																								contentFragmentRef={
																									contentQueryData?.content?.nodes?.[0]
																										?.archivedPageBannerStatusFragment
																								}
																								onPageRestore={contentQueryRefetch}
																							/>
																						}
																						contentId={contentId}
																						spaceKey={spaceKey || ''}
																						contentFragmentRef={
																							contentQueryData?.content?.nodes?.[0]
																								?.contentTitleWithByLine
																						}
																						meetingNotesAutomationDiscoveryFragmentRef={
																							contentQueryData?.content?.nodes?.[0]
																								?.meetingNotesAutomationDiscoveryFragment
																						}
																						bylineClassificationFragmentRef={
																							contentNodeWithBylineClassification?.bylineClassificationFragment
																						}
																						contentClassificationFragmentRef={
																							contentQueryData?.content?.nodes?.[0]
																								?.contentClassificationFragment
																						}
																						reactionsContentFragmentRef={
																							contentIdFromQuery === contentId &&
																							contentNode?.reactionsContentFragment &&
																							!StatsigConfigurations.isKillSwitchOn(
																								'confluence_vp_reactions_frag_reader',
																							) &&
																							expVal(
																								'confluence_exp_vp_document',
																								'isEnabled',
																								false,
																							)
																								? contentNode.reactionsContentFragment
																								: undefined
																						}
																						analyticsBylineFragmentRef={
																							contentQueryDataWithAnalyticsByline?.analyticsBylineFragment
																						}
																						hasDocumentContractAnalyticsByline
																						byLineContributors={
																							contentQueryData?.contentContributors
																						}
																						titleSpaceFragmentRef={contentQueryData?.space}
																						hasCoverPicture={hasCoverPicture}
																						pageWidthType={pageWidthType}
																						isEmbeddedPage={isEmbeddedPage}
																						shouldShowBylineReactions={shouldShowBylineReactions}
																						contentType={contentType}
																						contentSubType={contentSubType}
																						spaceId={spaceId}
																						{...titleAndByLineOptions}
																						hasByLine={hasByLine}
																					/>
																				</ReactionsProvider>
																			</AutoConversionByLineContextProvider>
																			<CurrentContentBodyDynamicTracker
																				contentId={contentId}
																				contentIdFromQuery={contentIdFromQuery}
																				contentBodyDynamic={contentBodyDynamic}
																				onCurrentContentBodyDynamicChange={
																					onCurrentContentBodyDynamicChange
																				}
																			/>
																			{/* Body of content */}
																			<ViewPageContentWithStatus
																				hasInlineComments={hasInlineComments}
																				hasProgressiveRendering={hasProgressiveRendering}
																				contentId={contentId}
																				data={contentQueryData}
																				error={contentQueryError}
																				spaceKey={spaceKey}
																				eventHandlerOverrides={eventHandlers}
																				appearance={appearance as RendererAppearance}
																				hasInlineActions={hasInlineActions}
																				lastModifiedDate={lastModifiedDate || undefined}
																				isEmbeddedPage={isEmbeddedPage}
																				customPerfData={customPerfData}
																				disableTableOverflowShadow={
																					expValEquals(
																						'platform_editor_table_css_overflow_shadow',
																						'isEnabled',
																						true,
																					)
																						? disableTableOverflowShadow
																						: true
																				}
																			/>
																			{/* https://product-fabric.atlassian.net/browse/COMMENTS-5436 */}
																			{hasInlineComments && !isFabricPage && (
																				<InlineCommentsHighlighterLoader pageId={contentId} />
																			)}
																			{isReadingAidsAutoHighlightEnabled && (
																				<ReadingAidsAcronymsHighlighter contentId={contentId} />
																			)}
																			{hasLabels &&
																				(isFabricPage ? null : (
																					<ViewPageLabelsLoader
																						pageWidthType={pageWidthType}
																						contentId={contentId}
																						contentType={contentType}
																						spaceKey={spaceKey}
																						spaceId={spaceId}
																						contentSubType={contentSubType}
																						contentFragmentRef={
																							contentQueryData?.content?.nodes?.[0]
																								?.viewPageLabels &&
																							!StatsigConfigurations.isKillSwitchOn(
																								'confluence_vp_meta_frag_reader',
																							) &&
																							expVal(
																								'confluence_exp_vp_document',
																								'isEnabled',
																								false,
																							)
																								? contentQueryData.content.nodes[0].viewPageLabels
																								: undefined
																						}
																					/>
																				))}
																			{/* Render all secondary content - likes, labels, comments, etc. */}
																			{shouldShowEOPRecommendations && (
																				<div
																					ref={endOfPageRecRef}
																					data-vc="eop-recommendations-wrapper"
																				>
																					<EndOfPageRecommendationFinalLoader
																						entityType={contentType || ''}
																						entityId={contentId}
																						spaceKey={spaceKey || ''}
																						pageWidthType={pageWidthType}
																					/>
																				</div>
																			)}
																			{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
																			{!process.env.REACT_SSR && (
																				<Fragment>
																					<ContentViewedEvent contentId={contentId} />
																					<CommentsDataBackgroundLoader contentId={contentId} />
																				</Fragment>
																			)}
																			{isFabricPage && (
																				<div
																					data-vc="object-sidebar-wrapper"
																					css={[hideDuringPdfPrint]}
																				>
																					<ViewPageObjectSidebarControl
																						isEmbeddedPage={isEmbeddedPage}
																						isLivePage={isLivePage}
																						contributorsFragmentRef={
																							!contentQueryError &&
																							contentStatus === 'current' &&
																							contentQueryData?.contentContributors
																								?.isCurrentUserContributor != null
																								? contentQueryData.contentContributors
																								: undefined
																						}
																						rovoActivationFragmentRef={
																							contentQueryData &&
																							'rovoActivationFragment' in contentQueryData
																								? (contentQueryData.rovoActivationFragment ??
																									undefined)
																								: undefined
																						}
																						hasCommentsButton
																						hasDetailsButton
																						hasLoomButton={contentStatus !== 'archived'}
																						hasAiButton={showAIButton}
																						hasRemixButton
																						hasAgentReceiptButton={!!agentTrustReceipt}
																						agentTrustReceipt={agentTrustReceipt}
																						contentId={contentId}
																						spaceKey={spaceKey}
																						contentType={contentType}
																						contentTitle={contentTitle}
																						lastModifiedDate={lastModifiedDate}
																						pageCommentsSectionInView={pageCommentsSectionInView}
																						contentIdFromQuery={contentIdFromQuery}
																						contentFragmentRef={commentsPanelPermissionFragmentRef}
																						contentBodyDynamic={contentBodyDynamic}
																						hasAudioButton={hasAudioButton}
																					/>
																				</div>
																			)}
																		</ViewPageMainContentContainer>
																		{/* Omni-bar: Persistent collaboration bar at bottom of page */}
																		{!isLivePage &&
																			!isPdfExport &&
																			!shouldSuppressOmnibarForSmallPage &&
																			!pageCommentsSectionInView &&
																			!endOfPageRecInView &&
																			isReactionsEnabled &&
																			omnibarEligibility.showReactions && (
																				<OmnibarEligibilityProvider value={omnibarEligibility}>
																					<ReactionsProvider contentId={contentId}>
																						<OmniBarWrapper />
																					</ReactionsProvider>
																				</OmnibarEligibilityProvider>
																			)}
																		{hasPageComments &&
																			/* Disable unsupported page comments when rendering live page as view page */ !isLivePage && (
																				<>
																					<StickyDraftCommentContainer
																						pageWidthType={pageWidthType}
																						key={`StickyDraftCommentContainer-${contentId}`}
																					/>
																					<UFOLabel name="renderer-footer-comments">
																						<div
																							ref={pageCommentsSectionRef}
																							data-vc="footer-comments"
																						>
																							<ObjectCommentWrapper
																								showLabels={Boolean(hasLabels) && isFabricPage}
																								pageWidthType={pageWidthType}
																								key={`ObjectCommentWrapper-${contentId}`} // ensures safe re-render on page change
																								hasReactions={hasReactions}
																								hasCommentSessions={hasCommentSessions}
																							/>
																						</div>
																					</UFOLabel>
																				</>
																			)}
																		<ViewCommentShortcutListener
																			isFabricPage={isFabricPage}
																			contentId={contentId}
																			preloadedCanAddComments={preloadedCanAddComments}
																		/>
																		<FooterLoader />
																		{isFabricPage && (
																			<PageCommentsPubSubHandler
																				contentId={contentId}
																				contentType={contentType}
																			/>
																		)}
																		{hasQuickReload &&
																			quickReloadReady &&
																			quickReloadLastPollTimeRef.current !== null && (
																				<ViewPageQuickReload
																					contentId={contentId}
																					contentType={contentType}
																					// The timestamp is initialized in a client-only effect to keep hydration deterministic.
																					// See notes in quick-reload
																					// package on what a more correct lastPollTime value would be.
																					lastPollTime={quickReloadLastPollTimeRef.current}
																					pageMode={PageMode.RENDERER}
																					isFabricPage={isFabricPage}
																					contentFragmentRef={commentsPanelPermissionFragmentRef}
																				/>
																			)}
																		{hasSatisfactionSurveys && (
																			<SatisfactionSurveysLoader
																				contentId={contentId}
																				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
																				isEditorExpEnabled={fg('cc_onboarding_experience')}
																			/>
																		)}
																		{contentType === 'page' && (
																			<LocalActivityViewPageTracker contentId={contentId} />
																		)}
																		{children}
																		{hasInlineComments && (
																			<ViewPageInlineComments
																				key={contentId}
																				loadingPriority={setLoadingPriority(hasInlineComments)}
																				contentId={contentId}
																				isFabricPage={isFabricPage}
																				preloadedCanAddComments={preloadedCanAddComments}
																			/>
																		)}
																		{contentType === 'page' && <ConvertToLivePageFlag />}
																		{expVal(
																			'confluence_modernized_move_dialog',
																			'isEnabled',
																			false,
																		) && <MovePageSuccessFlag />}
																	</Fragment>
																);
															}}
														</ViewPageContainer>
													</Fragment>
												)}
											</ChoreographerAssetsContextProvider>
										</AnnotationsProvider>
									</RendererActionsProvider>
								</ContentRefetchProvider>
							</DocumentUpdateStatusProvider>
						</PageCommentsCountProvider>
					</NewCommentsProvider>
				</ObjectCommentProvider>
			</ContentOwnershipContextProvider>
		</Fragment>
	);
};

export const ViewPageCommon: FC<Props> = (props) => (
	<OnboardingPersonalization contentId={props.contentId} spaceKey={props.spaceKey}>
		<ViewPageCommonInner {...props} />
	</OnboardingPersonalization>
);
